// Sicherheits-Limiter nach dem Mixed-Render (Audit, Low „Export").
//
// Der Mixed-Bus läuft seit #173 durch einen DynamicsCompressorNode
// (-1 dBFS, 20:1, 1 ms Attack). Das ist kein Brickwall: Transienten schneller
// als die Attack-Zeit rutschen durch, und `audioBufferToWav` klemmt danach hart
// auf ±1 — hörbares Clipping in Premix-WAV und MP4-Ton.
//
// Diese Stufe läuft auf dem fertig gerenderten PCM und ist ein echter
// Look-ahead-Limiter: Die Verstärkung ist an jeder Stelle bereits abgesenkt,
// BEVOR der Peak kommt (Minimum über das Vorausschau-Fenster, danach
// Box-Filter = lineare Absenkung über das Fenster), und steigt danach mit
// exponentiellem Release wieder an. Garantie: kein Sample über `ceiling`.
// Liegt der Peak schon unter der Decke (der Normalfall), wird nichts
// angefasst.
//
// Speicher: ein Float32Array über die Länge (Gain-Kurve) plus zwei kleine
// Ringpuffer — bei 3 min Stereo 48 kHz rund 35 MB, nur wenn wirklich
// limitiert werden muss.

/** -1 dBFS — dieselbe Decke wie der Kompressor davor. */
export const LIMITER_CEILING = Math.pow(10, -1 / 20);

export interface LimiterOptions {
  ceiling?: number;
  /** Vorausschau = Absenkzeit vor einem Peak. */
  lookaheadSec?: number;
  /** Zeitkonstante, mit der die Verstärkung nach dem Peak zurückkommt. */
  releaseSec?: number;
}

export interface LimiterResult {
  applied: boolean;
  /** Höchster Betrag vor dem Limiter (linear). */
  inputPeak: number;
}

export function measurePeak(channels: ReadonlyArray<Float32Array>): number {
  let peak = 0;
  for (const ch of channels) {
    for (let i = 0; i < ch.length; i++) {
      const a = ch[i] < 0 ? -ch[i] : ch[i];
      if (a > peak) peak = a;
    }
  }
  return peak;
}

/** Begrenzt die Kanäle in place auf `ceiling`. */
export function limitPeaks(
  channels: Float32Array[],
  sampleRate: number,
  opts: LimiterOptions = {},
): LimiterResult {
  const ceiling = opts.ceiling ?? LIMITER_CEILING;
  const n = channels.length > 0 ? channels[0].length : 0;
  const inputPeak = measurePeak(channels);
  if (n === 0 || inputPeak <= ceiling) return { applied: false, inputPeak };

  const L = Math.max(1, Math.round((opts.lookaheadSec ?? 0.005) * sampleRate));
  const releaseSamples = Math.max(1, (opts.releaseSec ?? 0.08) * sampleRate);
  const releaseCoef = 1 - Math.exp(-1 / releaseSamples);

  // 1) Benötigte Verstärkung je Sample (über alle Kanäle).
  const g = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let a = 0;
    for (let c = 0; c < channels.length; c++) {
      const v = channels[c][i];
      const abs = v < 0 ? -v : v;
      if (abs > a) a = abs;
    }
    g[i] = a > ceiling ? ceiling / a : 1;
  }

  // 2) Vorwärts-Minimum über [i, i+L-1], in place in `g` (monotone Deque als
  //    Ringpuffer). g[i] wird erst überschrieben, wenn i das Fenster verlässt.
  const cap = L + 1;
  const dq = new Int32Array(cap);
  let head = 0;
  let size = 0;
  const back = () => dq[(head + size - 1) % cap];
  for (let j = 0; j < n + L - 1; j++) {
    if (j < n) {
      while (size > 0 && g[back()] >= g[j]) size--;
      dq[(head + size) % cap] = j;
      size++;
    }
    const i = j - L + 1;
    if (i < 0) continue;
    while (size > 0 && dq[head] < i) {
      head = (head + 1) % cap;
      size--;
    }
    g[i] = g[dq[head]];
  }

  // 3) Box-Filter über die letzten L Minima (Absenkung linear über das
  //    Fenster; jedes Minimum im Fenster deckt den Peak ab → ≤ Bedarf),
  //    4) Release-Glättung nach oben, 5) anwenden.
  const ring = new Float32Array(L).fill(g[0]);
  let sum = L * g[0];
  let pos = 0;
  let r = 1;
  for (let i = 0; i < n; i++) {
    sum += g[i] - ring[pos];
    ring[pos] = g[i];
    pos = pos + 1 === L ? 0 : pos + 1;
    const h = sum / L;
    const rel = r + (1 - r) * releaseCoef;
    r = h < rel ? h : rel;
    for (let c = 0; c < channels.length; c++) {
      let v = channels[c][i] * r;
      // Rundungsreste der laufenden Summe (≈1e-7) dürfen die Decke nicht
      // reißen.
      if (v > ceiling) v = ceiling;
      else if (v < -ceiling) v = -ceiling;
      channels[c][i] = v;
    }
  }
  return { applied: true, inputPeak };
}
